import {
  FOGBUGZ_TO_ADO_ATTACHMENT_MAX_BYTES,
  FOGBUGZ_TO_ADO_BUG_SLOT_IDS,
  FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX,
  FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX,
  FOGBUGZ_TO_ADO_SLOT_LABELS,
  FOGBUGZ_TO_ADO_SLOT_VALUE_MAX,
  isFogbugzToAdoBugSlotId,
  type FogbugzBugBarMiss,
  type FogbugzToAdoAttachment,
  type FogbugzToAdoBarMode,
  type FogbugzToAdoBugSlotId,
  type FogbugzToAdoCaseBundle,
  type FogbugzToAdoClassification,
  type FogbugzToAdoDraft,
  type FogbugzToAdoEngineErrorCode,
  type FogbugzToAdoIteration,
  type FogbugzToAdoPmAddition,
  type FogbugzToAdoReporterQuestion,
  type FogbugzToAdoSlotVerdict,
  type FogbugzToAdoToolsetBridge,
} from "@ttc-spectree/toolbox-contract";

/**
 * Pure helpers of the FogBugz to ADO page (epic PI-SPEC-SW-E311, feature
 * PI-SPEC-SW-1640, ER-1666 Package E): engine refusals in plain words, slot
 * verdicts from a case's `intent.md`, the slot a Bug-bar rule asks about,
 * drafted reporter questions, PM edits as additions, the bar mode, the
 * iteration picker's order and the attachment checklist defaults. No I/O.
 */

// ---------------------------------------------------------------------------
// Bridge feature detection
// ---------------------------------------------------------------------------

/**
 * True when the app's `"fogbugz-to-ado"` bridge has every call the page uses.
 * An app that serves fogbugz-to-ado contract 1 always does; the check only
 * keeps a partial bridge from breaking the page. Pure.
 */
export function hasFogbugzToAdoBridge(bridge: FogbugzToAdoToolsetBridge | null | undefined): boolean {
  if (!bridge) return false;
  const partial = bridge as Partial<FogbugzToAdoToolsetBridge>;
  return (
    typeof partial.checkRepos === "function" &&
    typeof partial.readSlots === "function" &&
    typeof partial.draftBug === "function" &&
    typeof partial.cancel === "function"
  );
}

// ---------------------------------------------------------------------------
// Engine refusals
// ---------------------------------------------------------------------------

/** A plain message for an engine refusal, and whether Setup checks is where the PM fixes it. */
export interface FogbugzToAdoEngineMessage {
  text: string;
  setup: boolean;
}

const ENGINE_MESSAGES: Record<FogbugzToAdoEngineErrorCode, FogbugzToAdoEngineMessage> = {
  CLAUDE_NOT_FOUND: { text: "Claude Code is not installed on this computer.", setup: true },
  CLAUDE_NOT_SIGNED_IN: { text: "Claude Code is not signed in on this computer. Sign in, then try again.", setup: true },
  API_KEY_BILLING: {
    text: "Claude Code on this computer would bill an API key instead of your Claude subscription, so SpecTree Toolbox did not run it.",
    setup: true,
  },
  USAGE_LIMIT: { text: "Your Claude usage limit was reached. Try again once it resets.", setup: false },
  TIMED_OUT: { text: "Claude took too long, so it was stopped. Try again.", setup: false },
  PLUGIN_MISSING: {
    text: "The SpecTree plugin for Claude Code is missing on this computer, so Claude cannot read the case.",
    setup: true,
  },
  CANCELED: { text: "You stopped Claude.", setup: false },
  BUSY: {
    text: "SpecTree Toolbox is already working on a FogBugz case. Wait for it to finish, then try again.",
    setup: false,
  },
  REFUSED: {
    text: "SpecTree Toolbox stopped before Claude started, because it could not keep the run read-only and private on this computer.",
    setup: true,
  },
  NETWORK: { text: "Claude Code could not reach Anthropic. Check the network connection, then try again.", setup: false },
  UNKNOWN: { text: "Claude Code could not finish. Try again.", setup: false },
  REPOS_NOT_COPIED: {
    text: "The product's code is not on this computer yet. Open Setup checks to copy it, then try again.",
    setup: true,
  },
  REPOS_NOT_SET_UP: {
    text:
      "No code repositories are set up for this product in SpecTree. An Admin adds them on the Toolbox Repositories page, then try again.",
    setup: false,
  },
  REPO_LOCK_BUSY: {
    text: "The product's code is being updated by another run. Try again in a minute.",
    setup: false,
  },
  NO_SLOTS_RETURNED: {
    text: "Claude finished without saying what the case contains. Try again.",
    setup: false,
  },
  NO_DRAFT_RETURNED: { text: "Claude finished without a complete Bug draft. Try again.", setup: false },
};

/**
 * Plain words for a fogbugz-to-ado engine refusal. For a usage limit the
 * engine's own sentence (which names the reset time) is added; for REFUSED,
 * CLAUDE_NOT_FOUND, REPOS_NOT_COPIED and REPOS_NOT_SET_UP the engine's
 * sentence (what to fix) replaces the generic one. Every refusal ends with "Nothing was written to
 * ADO or FogBugz." Pure.
 */
export function fogbugzToAdoEngineMessage(
  code: FogbugzToAdoEngineErrorCode,
  engineMessage?: string,
): FogbugzToAdoEngineMessage {
  const base = ENGINE_MESSAGES[code];
  const detail = engineMessage?.trim();
  let text = base.text;
  if (code === "USAGE_LIMIT" && detail) text = `${base.text} ${detail}`;
  if ((code === "REFUSED" || code === "CLAUDE_NOT_FOUND" || code === "REPOS_NOT_COPIED" || code === "REPOS_NOT_SET_UP") && detail) {
    text = detail;
  }
  return { ...base, text: `${text} Nothing was written to ADO or FogBugz.` };
}

// ---------------------------------------------------------------------------
// Slots
// ---------------------------------------------------------------------------

/** `text` cut to `max` characters, trimmed. */
function cut(text: string, max: number): string {
  return text.trim().slice(0, max).trim();
}

/**
 * One verdict per bug slot from the case's `intent.md` slots: a filled slot
 * is `found` with its value and the evidence "intent.md"; anything else
 * (absent, or marked unknown) is `missing`, because unknown never fills a
 * required slot. Playbook order. Pure.
 */
export function verdictsFromIntent(intent: FogbugzToAdoCaseBundle["intent"]): FogbugzToAdoSlotVerdict[] {
  return FOGBUGZ_TO_ADO_BUG_SLOT_IDS.map((slot): FogbugzToAdoSlotVerdict => {
    const value = intent.slots?.[slot];
    if (value && value.state !== "unknown") {
      const text = cut(value.value, FOGBUGZ_TO_ADO_SLOT_VALUE_MAX);
      if (text !== "") return { slot, status: "found", value: text, evidence: "intent.md" };
    }
    return { slot, status: "missing" };
  });
}

/** The verdicts in playbook order (the order Claude returned them in may differ). Pure. */
export function orderedVerdicts(verdicts: readonly FogbugzToAdoSlotVerdict[]): FogbugzToAdoSlotVerdict[] {
  const order = new Map<string, number>(FOGBUGZ_TO_ADO_BUG_SLOT_IDS.map((slot, index) => [slot, index]));
  return [...verdicts].sort((a, b) => (order.get(a.slot) ?? 99) - (order.get(b.slot) ?? 99));
}

/** The grooming-bar rule ids of a Bug and the slot each asks about (the readiness rubric's `formulatorSlot`). */
const GROOMING_RULE_SLOTS: Readonly<Record<string, FogbugzToAdoBugSlotId>> = {
  numberedSteps: "reproSteps",
  expectedVsActual: "expectedVsActual",
  environmentStated: "environment",
  severityChosen: "severity",
};

/**
 * The bug slot a Bug-bar miss asks about: an Intent miss's rule id is the
 * slot id; a grooming-bar rule maps to its slot. Null for a rule this page
 * does not know. Pure.
 */
export function slotOfMiss(miss: Pick<FogbugzBugBarMiss, "ruleId">): FogbugzToAdoBugSlotId | null {
  if (isFogbugzToAdoBugSlotId(miss.ruleId)) return miss.ruleId;
  return GROOMING_RULE_SLOTS[miss.ruleId] ?? null;
}

/** The slots the bar still misses, each once, in the order the bar lists them. Pure. */
export function missingSlots(missing: readonly FogbugzBugBarMiss[]): FogbugzToAdoBugSlotId[] {
  const out: FogbugzToAdoBugSlotId[] = [];
  for (const miss of missing) {
    const slot = slotOfMiss(miss);
    if (slot !== null && !out.includes(slot)) out.push(slot);
  }
  return out;
}

/**
 * The questions for the reporter, one per slot the bar misses: Claude's
 * question for that slot when its slots run drafted one, else a template
 * from the bar's own plain message (always the case for an `intent.md`
 * case). Capped at `FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX`, each at
 * `FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX` characters. Pure.
 */
export function draftReporterQuestions(
  missing: readonly FogbugzBugBarMiss[],
  claudeQuestions: readonly FogbugzToAdoReporterQuestion[] = [],
): FogbugzToAdoReporterQuestion[] {
  const out: FogbugzToAdoReporterQuestion[] = [];
  for (const miss of missing) {
    const slot = slotOfMiss(miss);
    if (slot === null || out.some((question) => question.slot === slot)) continue;
    const fromClaude = claudeQuestions.find((question) => question.slot === slot)?.question.trim();
    const template = miss.message.replace(/^Not answered yet\.\s*/, "").trim() || miss.message.trim();
    const chosen = fromClaude !== undefined && fromClaude !== "" ? fromClaude : template;
    const question = cut(chosen !== "" ? chosen : `${FOGBUGZ_TO_ADO_SLOT_LABELS[slot]}?`, FOGBUGZ_TO_ADO_REPORTER_QUESTION_MAX);
    out.push({ slot, question });
    if (out.length >= FOGBUGZ_TO_ADO_REPORTER_QUESTIONS_MAX) break;
  }
  return out;
}

/**
 * Apply the PM's answers to the slots: an answered slot becomes `missing` in
 * the case (its found value, if any, did not meet the bar) and its answer a
 * PM addition, so the FogBugz note says exactly what the PM added. Earlier
 * additions for other slots are kept; a blank answer changes nothing, and so
 * does an answer that is still the slot's found value (the box starts from
 * it): that slot stays found, never credited to the PM. Pure.
 */
export function applyPmAnswers(
  slots: readonly FogbugzToAdoSlotVerdict[],
  pmAdditions: readonly FogbugzToAdoPmAddition[],
  answers: Partial<Record<FogbugzToAdoBugSlotId, string>>,
): { slots: FogbugzToAdoSlotVerdict[]; pmAdditions: FogbugzToAdoPmAddition[] } {
  const answered = new Map<FogbugzToAdoBugSlotId, string>();
  for (const slot of FOGBUGZ_TO_ADO_BUG_SLOT_IDS) {
    const text = cut(answers[slot] ?? "", FOGBUGZ_TO_ADO_SLOT_VALUE_MAX);
    const found = slots.find((verdict) => verdict.slot === slot && verdict.status === "found")?.value;
    if (text !== "" && text !== cut(found ?? "", FOGBUGZ_TO_ADO_SLOT_VALUE_MAX)) answered.set(slot, text);
  }
  const nextSlots = slots.map((verdict): FogbugzToAdoSlotVerdict =>
    answered.has(verdict.slot) ? { slot: verdict.slot, status: "missing" } : verdict,
  );
  const kept = pmAdditions.filter((addition) => !answered.has(addition.slot));
  const added = [...answered].map(([slot, text]): FogbugzToAdoPmAddition => ({ slot, text }));
  const order = new Map<string, number>(FOGBUGZ_TO_ADO_BUG_SLOT_IDS.map((slot, index) => [slot, index]));
  const nextAdditions = [...kept, ...added].sort((a, b) => (order.get(a.slot) ?? 99) - (order.get(b.slot) ?? 99));
  return { slots: nextSlots, pmAdditions: nextAdditions };
}

/** `complete` when the PM added nothing, `pm-filled` otherwise (the create body's rule). Pure. */
export function barModeOf(pmAdditions: readonly FogbugzToAdoPmAddition[]): FogbugzToAdoBarMode {
  return pmAdditions.length > 0 ? "pm-filled" : "complete";
}

// ---------------------------------------------------------------------------
// Draft editing
// ---------------------------------------------------------------------------

/** The repro steps as the review's text box shows them: one step per line. Pure. */
export function stepsToText(steps: readonly string[]): string {
  return steps.join("\n");
}

/**
 * A leading step marker the composer replaces with its own numbering: "1." or
 * "2)" not followed by a digit ("2.5 GB" is text, as in the Bug bar's
 * numbered-steps check), or a "-" / "*" / "•" bullet, each followed by a
 * space or the end of the line ("-v flag" is text).
 */
const STEP_MARKER = /^\s*(?:\d+[.)](?!\d)|[-*•])(?:\s+|$)/;

/**
 * The repro steps the PM typed, one per non-empty line, with a leading step
 * marker ({@link STEP_MARKER}) removed (the composer numbers them). Pure.
 */
export function textToSteps(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.replace(STEP_MARKER, "").trim())
    .filter((line) => line !== "");
}

/** A copy of `draft` (the review's starting point), so edits never touch the bridge's object. Pure. */
export function copyDraft(draft: FogbugzToAdoDraft): FogbugzToAdoDraft {
  return {
    ...draft,
    reproSteps: [...draft.reproSteps],
    evtxSummaries: draft.evtxSummaries.map((summary) => ({ ...summary })),
    screenshotDescriptions: draft.screenshotDescriptions.map((description) => ({ ...description })),
  };
}

// ---------------------------------------------------------------------------
// Pickers
// ---------------------------------------------------------------------------

/** One iteration picker option. */
export interface IterationOption {
  path: string;
  label: string;
  isRoot: boolean;
}

/**
 * The iteration picker's options: the project root first, labelled
 * "<project> (no sprint)" (the Bug goes to the backlog), then the sprints in
 * the order SpecTree lists them (newest first). Nothing is preselected: the
 * picker starts on its "Choose an iteration" prompt. Pure.
 */
export function iterationOptions(project: string, iterations: readonly FogbugzToAdoIteration[]): IterationOption[] {
  const roots = iterations.filter((iteration) => iteration.isRoot === true);
  const sprints = iterations.filter((iteration) => iteration.isRoot !== true);
  return [
    ...roots.map((iteration) => ({ path: iteration.path, label: `${project} (no sprint)`, isRoot: true })),
    ...sprints.map((iteration) => ({ path: iteration.path, label: iteration.path, isRoot: false })),
  ];
}

/**
 * The area picker's options: every area path of the project, plus the
 * link's default area path first when SpecTree did not list it (so the
 * prefill is visible and can be changed). Pure.
 */
export function areaOptions(classification: FogbugzToAdoClassification | undefined, defaultAreaPath: string | null): string[] {
  const paths = (classification?.areaPaths ?? []).map((area) => area.path);
  if (defaultAreaPath && !paths.includes(defaultAreaPath)) return [defaultAreaPath, ...paths];
  return paths;
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

/** True when a case file is larger than one ADO attachment may be, so it stays on the FogBugz case. Pure. */
export function attachmentTooLarge(attachment: Pick<FogbugzToAdoAttachment, "sizeBytes">): boolean {
  return attachment.sizeBytes > FOGBUGZ_TO_ADO_ATTACHMENT_MAX_BYTES;
}

/**
 * The attachments checked at the start of the review: every image and event
 * log not known to be over the per-file cap. Other files (such as
 * `intent.md`, already in the text) start unchecked. Pure.
 */
export function defaultAttachmentIndexes(attachments: readonly FogbugzToAdoAttachment[]): number[] {
  return attachments
    .filter((attachment) => (attachment.kind === "image" || attachment.kind === "evtx") && !attachmentTooLarge(attachment))
    .map((attachment) => attachment.index);
}

/** "1.2 MB", "340 KB", or "size not known" for 0 (FogBugz does not report sizes). Pure. */
export function sizeLabel(bytes: number): string {
  if (bytes <= 0) return "size not known";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${String(Math.max(1, Math.round(bytes / 1024)))} KB`;
}
